import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Calendar } from '@/components/ui/calendar';
import { es } from 'react-day-picker/locale';
import {
  VACATION_BLOCK_BRACKETS,
  VACATION_BLOCK_TABLE_HEADERS,
  computeVacationBlockDates,
  formatVacationBlockSentence,
  formatVacationImpactSentence,
  formatVacationDate,
  formatVacationDateShort,
} from '../../../utils/vacationBlockBracket';

/** Subconjunto de DateRange de react-day-picker que necesitamos */
type DateRange = { from?: Date; to?: Date };

const PAST_DATE_ERROR = 'La fecha no puede ser anterior a hoy';
const TO_BEFORE_FROM_ERROR = 'La fecha "hasta" no puede ser anterior a la fecha "desde"';

function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Convierte un objeto Date a string YYYY-MM-DD en timezone local. */
function dateToLocalStr(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Convierte YYYY-MM-DD → "dd/mm/aaaa" para mostrar en el input de texto. */
function isoToDisplay(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/** Parsea una cadena libre escrita por el usuario (acepta / o -) hacia YYYY-MM-DD. */
function parseUserDate(text: string): string | null {
  const normalized = text.trim().replace(/-/g, '/');
  const parts = normalized.split('/');
  if (parts.length !== 3) return null;
  const [rawD, rawM, rawY] = parts;
  if (!rawD || !rawM || !rawY) return null;
  const y = rawY.padStart(4, '20');
  const m = rawM.padStart(2, '0');
  const d2 = rawD.padStart(2, '0');
  if (y.length !== 4 || Number(y) < 2000) return null;
  const candidate = `${y}-${m}-${d2}`;
  const dt = new Date(candidate + 'T12:00:00');
  if (isNaN(dt.getTime())) return null;
  if (dateToLocalStr(dt) !== candidate) return null;
  return candidate;
}

/** Un período ya cargado, tal como lo necesita el chequeo de solapamiento. */
export interface ExistingVacationPeriod {
  id: string;
  from: string;
  to: string;
}

/**
 * Mismo criterio de solapamiento que valida el escenario de Make antes de crear
 * la vacación en api-core (±7 días de margen contra cualquier período ya
 * cargado) — ver memoria del proyecto. El escenario no devuelve error síncrono
 * en este caso (created falla en silencio), así que hay que frenarlo acá antes
 * de mandar el request, no solo confiar en la respuesta.
 */
const OVERLAP_BUFFER_DAYS = 7;

function addDays(iso: string, days: number): string {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + days);
  return dateToLocalStr(d);
}

function findOverlappingPeriod(
  from: string,
  to: string,
  periods: ExistingVacationPeriod[],
): ExistingVacationPeriod | null {
  for (const p of periods) {
    const bufferedFrom = addDays(p.from, -OVERLAP_BUFFER_DAYS);
    const bufferedTo = addDays(p.to, OVERLAP_BUFFER_DAYS);
    if (from <= bufferedTo && to >= bufferedFrom) return p;
  }
  return null;
}

export type VacationPeriodModalMode = 'create' | 'edit';

const COPY: Record<
  VacationPeriodModalMode,
  { title: string; description: string; confirmTitle: string; confirmLabel: string; confirmLoadingLabel: string }
> = {
  create: {
    title: 'Informar vacaciones y días off',
    description: 'Elegí las fechas de tu período. Vamos a bloquear tu agenda para pacientes nuevos según corresponda.',
    confirmTitle: 'Confirmá tu período',
    confirmLabel: 'Informar período',
    confirmLoadingLabel: 'Informando...',
  },
  edit: {
    title: 'Modificar período',
    description: 'Editá las fechas de inicio y fin de tu período.',
    confirmTitle: 'Confirmá el cambio',
    confirmLabel: 'Guardar cambios',
    confirmLoadingLabel: 'Guardando...',
  },
};

interface VacationPeriodFormModalProps {
  open: boolean;
  mode: VacationPeriodModalMode;
  /** Fechas iniciales YYYY-MM-DD — vacías para 'create'. */
  initialFrom?: string;
  initialTo?: string;
  saving: boolean;
  onClose: () => void;
  onConfirm: (from: string, to: string) => void | Promise<void>;
  /** Períodos ya cargados (sin cancelar, y sin el que se está editando en modo 'edit') — para el chequeo de solapamiento. */
  existingPeriods?: ExistingVacationPeriod[];
  /** Modo demo del recorrido virtual de "Vacaciones y Días off": el modal se abre con un
   *  período ficticio ya cargado (ver ProfileVacationsTab) para mostrar el cuadro de
   *  consecuencias en vivo, pero queda inerte — sin cerrar por click afuera/Escape, sin
   *  interacción en fechas/checkbox, y sin botones de acción (el avance lo maneja el
   *  tour, no este modal). */
  tourDemo?: boolean;
}

/**
 * Modal de 2 pasos (fechas + consecuencias → confirmación de impacto) compartido
 * por "Informar vacaciones" (alta nativa) y "Modificar período" (edición ya
 * existente) — mismo UX, misma regla de negocio (`vacationBlockBracket.ts`),
 * distinto título/copy y distinto handler de guardado (`onConfirm`, inyectado
 * por el caller). Extraído de `ProfileVacationsTab.tsx` para no duplicar esta
 * UI al sumar el alta nativa.
 */
export const VacationPeriodFormModal: React.FC<VacationPeriodFormModalProps> = ({
  open,
  mode,
  initialFrom = '',
  initialTo = '',
  saving,
  onClose,
  onConfirm,
  existingPeriods = [],
  tourDemo = false,
}) => {
  const copy = COPY[mode];

  const [editFrom, setEditFrom] = useState('');
  const [editTo, setEditTo] = useState('');
  const [editFromText, setEditFromText] = useState('');
  const [editToText, setEditToText] = useState('');
  const [editFromError, setEditFromError] = useState('');
  const [editToError, setEditToError] = useState('');
  const [vacationConsentChecked, setVacationConsentChecked] = useState(false);
  const [modalStep, setModalStep] = useState<1 | 2>(1);
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());

  const syncModalState = (fromIso: string, toIso: string) => {
    setEditFrom(fromIso);
    setEditTo(toIso);
    setEditFromText(isoToDisplay(fromIso));
    setEditToText(isoToDisplay(toIso));
    setEditFromError('');
    setEditToError('');
    setCalendarMonth(fromIso ? new Date(fromIso + 'T12:00:00') : new Date());
    setVacationConsentChecked(false);
  };

  // Al abrir, arranca desde las fechas iniciales (vacías en 'create'); al
  // cerrar, resetea todo — así la próxima apertura no arrastra estado viejo.
  useEffect(() => {
    if (open) {
      syncModalState(initialFrom, initialTo);
      setModalStep(1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [open, initialFrom, initialTo]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleFromTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setEditFromText(text);
    setEditFromError('');
    const iso = parseUserDate(text);
    if (iso) {
      if (iso < localToday()) {
        setEditFromError(PAST_DATE_ERROR);
      } else {
        setEditFrom(iso);
        if (editTo && iso > editTo) {
          setEditTo('');
          setEditToText('');
          setEditToError('');
        }
      }
    }
  };

  const handleFromTextBlur = () => {
    const iso = parseUserDate(editFromText);
    if (editFromText && !iso) {
      setEditFromText(isoToDisplay(editFrom));
      setEditFromError('');
    } else if (iso && iso < localToday()) {
      setEditFromError(PAST_DATE_ERROR);
    }
  };

  const handleToTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setEditToText(text);
    setEditToError('');
    const iso = parseUserDate(text);
    if (iso) {
      if (iso < localToday()) {
        setEditToError(PAST_DATE_ERROR);
      } else if (editFrom && iso < editFrom) {
        setEditToError(TO_BEFORE_FROM_ERROR);
      } else {
        setEditTo(iso);
        setVacationConsentChecked(false);
      }
    }
  };

  const handleToTextBlur = () => {
    const iso = parseUserDate(editToText);
    if (editToText && !iso) {
      setEditToText(isoToDisplay(editTo));
      setEditToError('');
    } else if (iso && iso < localToday()) {
      setEditToError(PAST_DATE_ERROR);
    } else if (iso && editFrom && iso < editFrom) {
      setEditToError(TO_BEFORE_FROM_ERROR);
    }
  };

  const handleCalendarSelect = (range: DateRange | undefined) => {
    const todayStr = localToday();
    const newFrom = range?.from ? dateToLocalStr(range.from) : '';
    const newTo = range?.to ? dateToLocalStr(range.to) : '';
    if (newFrom && newFrom < todayStr) return;
    if (newTo && newTo < todayStr) return;
    syncModalState(newFrom, newTo);
  };

  const overlappingPeriod =
    editFrom && editTo ? findOverlappingPeriod(editFrom, editTo, existingPeriods) : null;

  const canContinue =
    !!editFrom && !!editTo && !editFromError && !editToError && !overlappingPeriod && vacationConsentChecked;

  return (
    // modal={false} en tourDemo: Radix desactiva pointer-events en <body> mientras un
    // Dialog "modal" (default) está abierto, para forzar que solo se pueda interactuar
    // con su contenido — pero el tooltip de Joyride se renderiza fuera de este Dialog
    // (portal aparte, agregado a <body>), así que quedaba con sus botones "Continuar"/
    // "Atrás" inclickeables. Con modal={false} Radix no toca pointer-events globales;
    // el modal igual queda inerte por el pointer-events-none propio de acá abajo.
    <Dialog open={open} modal={!tourDemo} onOpenChange={(o) => { if (!o && !tourDemo) handleClose(); }}>
      <DialogContent
        className={
          modalStep === 1
            ? 'sm:max-w-2xl lg:max-w-4xl xl:max-w-5xl w-full p-0 overflow-hidden'
            : 'sm:max-w-lg w-full p-0 overflow-hidden'
        }
      >
        {modalStep === 1 && (
          <div className={`px-6 pt-6 pb-2 max-h-[85vh] overflow-y-auto ${tourDemo ? 'pointer-events-none select-none' : ''}`}>
            <DialogHeader>
              <DialogTitle className="text-base">{copy.title}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500 mt-1 mb-5">{copy.description}</p>

            <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
              {/* Columna izquierda: inputs de fecha + calendario. Ancho fijo en
                  desktop (el calendario no estira) para dejarle el resto del
                  ancho a la tabla de consecuencias, en vez de apilar todo. */}
              <div className="flex flex-col gap-3 lg:w-[320px] lg:flex-shrink-0">
                <div className="flex gap-3">
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Desde</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="dd/mm/aaaa"
                      value={editFromText}
                      onChange={handleFromTextChange}
                      onBlur={handleFromTextBlur}
                      aria-label="Fecha desde"
                      aria-invalid={!!editFromError}
                      className={[
                        'w-full text-sm px-3 py-2.5 rounded-lg border focus:outline-none focus:ring-2 transition-colors',
                        editFromError
                          ? 'border-red-400 bg-red-50 text-red-700 focus:ring-red-200 focus:border-red-500'
                          : 'border-gray-200 bg-white text-gray-800 focus:ring-[#6D57BC]/25 focus:border-[#6D57BC]',
                      ].join(' ')}
                    />
                    {editFromError && (
                      <p className="text-xs text-red-600 mt-0.5" role="alert">{editFromError}</p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Hasta</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="dd/mm/aaaa"
                      value={editToText}
                      onChange={handleToTextChange}
                      onBlur={handleToTextBlur}
                      aria-label="Fecha hasta"
                      aria-invalid={!!editToError}
                      className={[
                        'w-full text-sm px-3 py-2.5 rounded-lg border focus:outline-none focus:ring-2 transition-colors',
                        editToError
                          ? 'border-red-400 bg-red-50 text-red-700 focus:ring-red-200 focus:border-red-500'
                          : 'border-gray-200 bg-white text-gray-800 focus:ring-[#6D57BC]/25 focus:border-[#6D57BC]',
                      ].join(' ')}
                    />
                    {editToError && (
                      <p className="text-xs text-red-600 mt-0.5" role="alert">{editToError}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-center rounded-xl border border-gray-200 p-2">
                  <Calendar
                    mode="range"
                    locale={es}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    selected={{
                      from: editFrom ? new Date(editFrom + 'T12:00:00') : undefined,
                      to: editTo ? new Date(editTo + 'T12:00:00') : undefined,
                    }}
                    onSelect={handleCalendarSelect}
                    disabled={(date: Date) => dateToLocalStr(date) < localToday()}
                    classNames={{
                      disabled: 'text-muted-foreground opacity-30 pointer-events-none cursor-default',
                      caption_label: 'text-sm font-medium capitalize',
                    }}
                  />
                </div>
              </div>

              {/* Columna derecha: consecuencias. En mobile queda debajo del
                  calendario (stack natural); en desktop usa el ancho libre
                  para que la tabla entre completa sin scrollear. */}
              <div className="flex-1 min-w-0">
                {(() => {
                  const fromDate = editFrom ? new Date(editFrom + 'T12:00:00') : null;
                  const toDate = editTo ? new Date(editTo + 'T12:00:00') : null;
                  const blockResult = fromDate && toDate ? computeVacationBlockDates(fromDate, toDate) : null;
                  const activeRow = blockResult ? VACATION_BLOCK_BRACKETS.indexOf(blockResult.bracket) : null;
                  const detailSentence = fromDate && toDate ? formatVacationBlockSentence(fromDate, toDate, formatVacationDateShort) : null;
                  return (
                    <div id={tourDemo ? 'vacation-tour-consequences' : undefined} className="flex flex-col gap-3">
                      {/* mb-8 además del gap-3 del padre: deja lugar para que el tooltip de los
                          headers (abre hacia arriba) no tape este texto — en el layout de 2
                          columnas quedan mucho más cerca que en el stack vertical de antes. */}
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider m-0 mb-8">
                        Consecuencias de tu solicitud
                      </p>
                      <div className="overflow-x-auto rounded-xl border border-gray-200">
                        <table className="w-full text-xs table-fixed">
                          <colgroup>
                            <col className="w-[20%]" />
                            <col className="w-[34%]" />
                            <col className="w-[46%]" />
                          </colgroup>
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="text-left font-semibold text-gray-600 px-3 py-2 border-b border-r border-gray-200">{VACATION_BLOCK_TABLE_HEADERS.days}</th>
                              <th className="text-left font-semibold text-gray-600 px-3 py-2 border-b border-r border-gray-200">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="border-b border-dotted border-gray-400 cursor-help">
                                      {VACATION_BLOCK_TABLE_HEADERS.before}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom" className="text-center">
                                    Durante este período no se te asignarán pacientes nuevos. No afecta la atención de tus pacientes activos, que seguís atendiendo con normalidad.
                                  </TooltipContent>
                                </Tooltip>
                              </th>
                              <th className="text-left font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="border-b border-dotted border-gray-400 cursor-help">
                                      {VACATION_BLOCK_TABLE_HEADERS.after}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom" className="text-center">
                                    Indica si tu agenda vuelve a recibir pacientes nuevos apenas regresás, o si el bloqueo continúa un tiempo más después de tu regreso, según la duración de tu ausencia.
                                  </TooltipContent>
                                </Tooltip>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {VACATION_BLOCK_BRACKETS.map((bracket, i) => {
                              const isActive = activeRow === i;
                              return (
                                <tr key={bracket.key} className={isActive ? 'bg-[#F0EEFB]' : undefined}>
                                  <td className={`px-3 py-2 leading-snug border-r border-gray-200 ${isActive ? 'text-[#5a47a8] font-semibold' : 'text-gray-700'}`}>
                                    <div className="flex items-start gap-1.5">
                                      {isActive && <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#6D57BC] inline-block" />}
                                      <span>{bracket.label}</span>
                                    </div>
                                  </td>
                                  <td className={`px-3 py-2 leading-snug border-r border-gray-200 ${isActive ? 'text-[#5a47a8] font-medium' : 'text-gray-600'}`}>{bracket.blockBeforeLabel}</td>
                                  <td className={`px-3 py-2 leading-snug ${isActive ? 'text-[#5a47a8] font-medium' : 'text-gray-600'}`}>{bracket.blockAfterLabel}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {detailSentence && (
                        <p className="text-xs text-[#6D57BC] m-0 flex items-start gap-1.5">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#6D57BC] flex-shrink-0 inline-block" />
                          <span>{detailSentence}</span>
                        </p>
                      )}
                      {overlappingPeriod && (
                        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5" role="alert">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0 inline-block" />
                          <p className="text-xs text-amber-800 m-0 leading-snug">
                            Este período se superpone (o queda a menos de 7 días) de otro que ya tenés cargado:
                            {' '}
                            <strong>{isoToDisplay(overlappingPeriod.from)} → {isoToDisplay(overlappingPeriod.to)}</strong>.
                            {' '}Ajustá las fechas para continuar.
                          </p>
                        </div>
                      )}
                      <label htmlFor={`vacation-consent-checkbox-${mode}`} className="flex items-start gap-3 cursor-pointer select-none">
                        <input
                          id={`vacation-consent-checkbox-${mode}`}
                          type="checkbox"
                          checked={vacationConsentChecked}
                          onChange={(e) => setVacationConsentChecked(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-purple-600 cursor-pointer flex-shrink-0"
                        />
                        <span className="text-sm text-gray-700">Estoy de acuerdo con lo informado arriba</span>
                      </label>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {modalStep === 1 && !tourDemo && (
          <div className="flex justify-end gap-2 px-6 py-5 border-t border-gray-100">
            <Button variant="outline" onClick={handleClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={() => setModalStep(2)}
              disabled={!canContinue}
              className="bg-[#6D57BC] text-white hover:bg-[#5a47a8]"
            >
              Continuar
            </Button>
          </div>
        )}

        {modalStep === 2 && (() => {
          const fromDate = editFrom ? new Date(editFrom + 'T12:00:00') : null;
          const toDate = editTo ? new Date(editTo + 'T12:00:00') : null;
          const impactSentence = fromDate && toDate ? formatVacationImpactSentence(fromDate, toDate, formatVacationDate) : null;
          return (
            <>
              <div className="px-6 pt-6 pb-4">
                <DialogHeader>
                  <DialogTitle className="text-base">{copy.confirmTitle}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-gray-500 mt-1 mb-4">
                  Este es el impacto real en tu agenda con las fechas que elegiste.
                </p>
                <div className="rounded-2xl bg-[#F0EEFB] border border-[#D6CFF5] px-4 py-4">
                  <p className="text-sm text-[#5a47a8] font-medium m-0 leading-relaxed">{impactSentence}</p>
                </div>
              </div>
              <div className="flex justify-end gap-2 px-6 py-5 border-t border-gray-100">
                <Button variant="outline" onClick={() => setModalStep(1)} disabled={saving}>
                  Atrás
                </Button>
                <Button
                  onClick={() => onConfirm(editFrom, editTo)}
                  disabled={saving}
                  className="bg-[#6D57BC] text-white hover:bg-[#5a47a8]"
                >
                  {saving ? copy.confirmLoadingLabel : copy.confirmLabel}
                </Button>
              </div>
            </>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
};

export default VacationPeriodFormModal;
